import React, { useState, useEffect, useContext, useRef } from "react";
import { API_URL } from "../components/api";
import "bootstrap/dist/css/bootstrap.css";
import Axios from "axios";
import Select from "react-select";
import AuthContext from "../context/AuthContext";

function IPTVSubAdd() {
  const { user } = useContext(AuthContext);
  const authTokens = JSON.parse(localStorage.getItem("authTokens"));

  const [clientsRaw, setClientsRaw] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [usernameOptions, setUsernameOptions] = useState([]);

  const [client, setClient] = useState(null);
  const [username, setUsername] = useState(null); // optional

  const [tvCount, setTvCount] = useState(null);
  const [deviceNames, setDeviceNames] = useState([]); // ["TV 1", "TV 2", ...]
  const [iptvAccountOptions, setIptvAccountOptions] = useState([]);
  const [iptvAccountSelected, setIptvAccountSelected] = useState(null);

  const [loadingOnboarding, setLoadingOnboarding] = useState(true);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isMountedRef = useRef(true);

  const authHeaders = {
    Authorization: `Bearer ${authTokens?.access}`,
    "Content-Type": "application/json",
  };

  const customFilter = (option, inputValue) => {
    const words = inputValue.toLowerCase().split(" ").filter(Boolean);
    const label = (option?.label || "").toLowerCase();
    return words.every((w) => label.includes(w));
  };

  const fetchOnboarding = async () => {
    try {
      setLoadingOnboarding(true);
      const res = await Axios.get(
        `${API_URL}/radius/subscriptions/clients/onboarding/`,
        { headers: authHeaders, params: { username: user?.username } }
      );

      if (res.status === 200 && isMountedRef.current) {
        const rows = Array.isArray(res.data) ? res.data : [];
        setClientsRaw(rows);

        const uniqueClients = [
          ...new Map(
            rows.map((item) => [
              item.client_id,
              { label: item.fullname, value: item.client_id },
            ])
          ).values(),
        ];
        setClientOptions(uniqueClients);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) setLoadingOnboarding(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchOnboarding();
    return () => {
      isMountedRef.current = false;
    };
  }, [authTokens?.access, user?.username]);

  const handleClientChange = (opt) => {
    setClient(opt);
    setUsername(null);
    setTvCount(null);
    setDeviceNames([]);
    setIptvAccountOptions([]);
    setIptvAccountSelected(null);

    if (!opt) {
      setUsernameOptions([]);
      return;
    }

    const filtered = clientsRaw
      .filter((row) => row.client_id === opt.value)
      .map((row) => ({
        label: row.username,
        value: row.subscription_id,
      }));

    const uniqueUsernames = [
      ...new Map(filtered.map((u) => [u.label, u])).values(),
    ];
    setUsernameOptions(uniqueUsernames);
  };

  const handleTvCountChange = async (opt) => {
    setTvCount(opt);
    setIptvAccountSelected(null);

    // Adjust device name fields to match TV count, preserving existing entries
    const count = opt?.value || 0;
    setDeviceNames((prev) => {
      const next = prev.slice(0, count);
      while (next.length < count) next.push("");
      return next;
    });

    if (!opt?.value) {
      setIptvAccountOptions([]);
      return;
    }
    await fetchIptvAccounts(opt.value);
  };

  const fetchIptvAccounts = async (tvs) => {
    try {
      setLoadingAccounts(true);
      const res = await Axios.get(
        `${API_URL}/radius/subscriptions/iptv/sub/account/`,
        {
          headers: authHeaders,
          params: {
            tvs,
            username: user?.username,
          },
        }
      );

      if (res.status === 200 && isMountedRef.current) {
        const rows = Array.isArray(res.data) ? res.data : [];
        const opts = rows.map((r) => ({
          value: r.id ?? r.iptv_account_id ?? r.iptv_id,
          label: r.name ?? r.username ?? `IPTV #${r.id ?? r.iptv_id ?? "-"}`,
        }));
        setIptvAccountOptions(opts);
      }
    } catch (e) {
      console.error("Failed to fetch IPTV accounts:", e);
      if (isMountedRef.current) setIptvAccountOptions([]);
    } finally {
      if (isMountedRef.current) setLoadingAccounts(false);
    }
  };

  const updateDeviceName = (i, v) => {
    setDeviceNames((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!client?.value || !tvCount?.value || !iptvAccountSelected?.value) return;

    // Trim + validate device names
    const trimmed = deviceNames.map((n) => (n || "").trim());
    const allFilled =
      (tvCount?.value || 0) > 0 &&
      trimmed.length === tvCount.value &&
      trimmed.every((n) => n.length > 0);
    if (!allFilled) return;

    // optional username/subscription_id
    const subscriptionId = username?.value || null;

    // Build a robust payload; backend can use either structure
    const devices = trimmed.map((name, idx) => ({
      index: idx + 1,
      name,
    }));

    setSubmitting(true);
    try {
      await Axios.post(
        `${API_URL}/radius/subscriptions/iptv/sub/account/`,
        {
          Action: "Post",
          client_id: client.value,
          subscription_id: subscriptionId, // optional
          tvs: tvCount.value,
          iptv_account_id: iptvAccountSelected.value,
          device_names: trimmed, // simple array
          devices,               // structured array [{index, name}]
          userdealer: user?.username,
        },
        { headers: authHeaders }
      );

      // reset fields after success
      setTvCount(null);
      setDeviceNames([]);
      setIptvAccountOptions([]);
      setIptvAccountSelected(null);
      setUsername(null);
    } catch (err) {
      console.error("Failed to link IPTV subscription:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const tvCountValid = !!tvCount?.value;
  const deviceNamesValid =
    tvCountValid &&
    deviceNames.length === tvCount.value &&
    deviceNames.every((n) => (n || "").trim().length > 0);

  const canSubmit =
    !!client?.value &&
    tvCountValid &&
    !!iptvAccountSelected?.value &&
    deviceNamesValid;

  const tvCountOptions = [
    { label: "1 Device", value: 1 },
    { label: "2 Devices", value: 2 },
    { label: "3 Devices", value: 3 },
  ];

  return (
    <div className="p-2">
      <div className="row g-3">
        {/* Client */}
        <div className="col-12">
          <label className="form-label">Client</label>
          <Select
            className="search-line"
            placeholder={loadingOnboarding ? "Loading clients…" : "Choose Client"}
            options={clientOptions}
            onChange={handleClientChange}
            filterOption={customFilter}
            isSearchable
            value={client}
            isClearable
            isDisabled={loadingOnboarding}
          />
        </div>

        {/* Username (optional) */}
        <div className="col-12">
          <label className="form-label">Username (Optional)</label>
          <Select
            className="search-line"
            placeholder={client ? "Choose Username (optional)" : "Select a client first"}
            options={usernameOptions}
            onChange={setUsername}
            filterOption={customFilter}
            isSearchable
            value={username}
            isDisabled={!client || !usernameOptions.length}
            isClearable
          />
        </div>

        {/* TVs count */}
        <div className="col-12">
          <label className="form-label">Number of Devices</label>
          <Select
            className="search-line"
            placeholder="Select 1, 2 or 3 Devices"
            options={tvCountOptions}
            onChange={handleTvCountChange}
            value={tvCount}
            isClearable
            isDisabled={!client}
          />
        </div>

        {/* Device names — rendered dynamically based on tvCount */}
        {tvCount?.value ? (
          <div className="col-12">
            <div className="row g-2">
              {Array.from({ length: tvCount.value }).map((_, i) => {
                const id = `device-name-${i}`;
                const value = deviceNames[i] ?? "";
                const invalid = (value || "").trim().length === 0;
                return (
                  <div className="col-12" key={id}>
                    <label className="form-label" htmlFor={id}>
                      Device #{i + 1} Name
                    </label>
                    <input
                      id={id}
                      type="text"
                      className={`form-control ${invalid ? "is-invalid" : ""}`}
                      placeholder={`e.g., Living Room TV ${i + 1}`}
                      value={value}
                      onChange={(e) => updateDeviceName(i, e.target.value)}
                    />
                    {invalid && (
                      <div className="invalid-feedback">
                        Please enter a device name.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* IPTV Account */}
        <div className="col-12">
          <label className="form-label">IPTV Account</label>
          <Select
            className="search-line"
            placeholder={
              tvCount?.value
                ? loadingAccounts
                  ? "Loading IPTV accounts…"
                  : "Choose IPTV account"
                : "Select the number of TVs first"
            }
            options={iptvAccountOptions}
            onChange={setIptvAccountSelected}
            filterOption={customFilter}
            isSearchable
            value={iptvAccountSelected}
            isDisabled={!tvCount?.value || loadingAccounts || !iptvAccountOptions.length}
            isMulti={false}
            closeMenuOnSelect
          />
        </div>

        {/* Submit */}
        <div className="col-12 text-end">
          <button
            className="btn btn-primary"
            type="button"
            disabled={!canSubmit || submitting}
            onClick={handleSubmit}
          >
            {submitting ? "Linking…" : "Link IPTV"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default IPTVSubAdd;
